import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { makeStyles } from "@material-ui/core/styles";
import { P, COMPARE_LABEL, PD, FOC_DETAILS, BUZZOUT_DETAILS, OVERSTAY_DETAILS } from "./constants";
import { fmt, ini } from "./helpers";
import { Kpi, Card, Badge, ProgBar, InsightsChart } from "./ui";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12, position: "relative" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: 8 },
  mainGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  // Leakage Breakdown -- "to beat" panel
  toBeatPanel: { background: "#E6F4E8", borderRadius: 7, padding: "8px 12px", marginBottom: 10, border: "0.5px solid #3EAC4D" },
  toBeatTitle: { fontWeight: 600, color: "#2E7D32", marginBottom: 4, letterSpacing: ".06em" },
  toBeatList: { display: "flex", flexDirection: "column", gap: 3 },
  toBeatRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  toBeatText: { color: "#2E7D32" },
  toBeatGain: { fontWeight: 600, color: "#2E7D32", marginLeft: 8, flexShrink: 0 },

  // Category rows
  catRow: { marginBottom: 10, cursor: "pointer", borderRadius: 7, padding: "8px 10px" },
  catHead: { display: "flex", justifyContent: "space-between", marginBottom: 4 },
  catLeft: { display: "flex", alignItems: "center", gap: 6 },
  catBar: { width: 3, height: 14, borderRadius: 2, display: "inline-block" },
  catLabel: { fontWeight: 500, color: "var(--color-text-primary)" },
  catRight: { display: "flex", alignItems: "center", gap: 8 },
  catVal: { fontWeight: 600, color: P.headline },
  catPct: { color: "var(--color-text-tertiary)" },
  catChevron: { color: "var(--color-text-tertiary)" },

  // Drill tables
  drillWrap: { marginTop: 10, overflowX: "auto" },
  drillTitle: { fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 6, letterSpacing: ".06em" },
  tableMin520: { minWidth: 520 },
  headCell: { fontWeight: 600, color: "var(--color-text-secondary)", padding: "3px 6px", borderBottom: "0.5px solid var(--color-border-tertiary)" },
  bodyRow: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  cell: { padding: "5px 6px", borderBottom: "none" },
  cellSecondary: { padding: "5px 6px", borderBottom: "none", color: "var(--color-text-secondary)" },
  actualText: { color: P.nv, fontWeight: 500 },
  monoRef: { fontFamily: "var(--font-mono)", color: P.nv },
  monoRefIndigo: { fontFamily: "var(--font-mono)", color: "#3127B8" },
  staffCellInner: { display: "flex", alignItems: "center", gap: 4 },
  staffAvatar: { width: 18, height: 18, borderRadius: "50%", background: "#E6F4E8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#2E7D32" },
  plateChip: { fontFamily: "var(--font-mono)", background: "var(--color-background-secondary)", padding: "1px 4px", borderRadius: 3 },
  lostCell: { padding: "5px 6px", borderBottom: "none", fontWeight: 600, color: P.nv, fontFamily: "var(--font-mono)" },
  reasonCell: { padding: "5px 6px", borderBottom: "none", color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  reasonCell140: { maxWidth: 140 },
  reasonCell120: { maxWidth: 120 },
  overstayVal: { padding: "5px 6px", borderBottom: "none", fontWeight: 600, color: P.nv },

  // Front Desk Leakage card
  howPanel: { background: "#E6F4E8", borderRadius: 8, padding: "10px 14px", marginBottom: 12, border: "0.5px solid #BFE3C6" },
  howTitle: { fontWeight: 600, color: "#2E7D32", marginBottom: 6, letterSpacing: ".04em" },
  howBody: { color: "#2E7D32" },
  fdGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 },
  fdTile: { background: "var(--color-background-secondary)", borderRadius: 7, padding: "10px 12px", border: "0.5px solid var(--color-border-tertiary)" },
  fdTileLabel: { color: "var(--color-text-secondary)", marginBottom: 4 },
  fdTileVal: { fontWeight: 800, fontFamily: "var(--font-mono)" },
  fdTileSub: { color: "var(--color-text-tertiary)", marginTop: 2 },
  impactSection: { borderTop: "0.5px solid var(--color-border-tertiary)", paddingTop: 10 },
  impactRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  impactLabel: { fontWeight: 600, color: "var(--color-text-secondary)" },
  impactVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  impactGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
  impactItem: { display: "flex", justifyContent: "space-between", padding: "5px 8px", background: "var(--color-background-secondary)", borderRadius: 5, border: "0.5px solid var(--color-border-tertiary)" },
  impactItemLabel: { color: "var(--color-text-secondary)" },
  impactItemVal: { fontWeight: 600, color: P.nv },
  actionPanel: { marginTop: 8, padding: "7px 10px", background: "#E6F4E8", borderRadius: 6, border: "0.5px solid #BFE3C6", color: "#2E7D32" },

  // Buzz-out Repeat Offenders table
  table: { width: "100%" },
  headRow: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  headCellSpaced: { fontWeight: 600, color: "var(--color-text-secondary)", padding: "4px 8px", letterSpacing: ".04em", borderBottom: "none" },
  bigCell: { padding: "8px 8px", borderBottom: "none" },
  bigCellMono: { padding: "8px 8px", borderBottom: "none", fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  bigCellSecondary: { padding: "8px 8px", borderBottom: "none", color: "var(--color-text-secondary)" },
  plateBadge: { fontFamily: "var(--font-mono)", fontWeight: 700, color: "#2E7D32", background: "#E6F4E8", padding: "2px 7px", borderRadius: 4 },
  countBadge: { marginLeft: 5, fontWeight: 700, color: "#fff", background: P.nv, padding: "1px 5px", borderRadius: 3 },
  ticketSentText: { color: "#241C8A", fontWeight: 600 },
  flagBtn: { textTransform: "none", color: "#fff", backgroundColor: P.nv, fontWeight: 600, "&:hover": { backgroundColor: "#241C8A" } },
  borderlineText: { color: "var(--color-text-tertiary)" },

  // Leakage Trend
  trendChart: { height: 180 },

  // Sanctioned Plate Usage
  sanctGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 },
  sanctTile: { background: "var(--color-background-secondary)", borderRadius: 6, padding: "7px 9px", border: "0.5px solid var(--color-border-tertiary)" },
  sanctTileLabel: { color: "var(--color-text-secondary)", marginBottom: 2 },
  sanctTileVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  sanctCell: { padding: "6px 6px", borderBottom: "none" },
  sanctPlate: { fontFamily: "var(--font-mono)", fontWeight: 700, color: P.nv },
  sanctLabel: { padding: "6px 6px", borderBottom: "none", color: "var(--color-text-primary)", fontWeight: 500 },
  sanctMono: { padding: "6px 6px", borderBottom: "none", fontFamily: "var(--font-mono)", color: P.headline },
  sanctMonoSecondary: { padding: "6px 6px", borderBottom: "none", fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" },
  sanctMonoBold: { padding: "6px 6px", borderBottom: "none", fontWeight: 600, fontFamily: "var(--font-mono)", color: P.headline },

  // Ticket modal
  overlay: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, minHeight: "100vh", background: "rgba(0,0,0,0.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "var(--color-background-primary)", borderRadius: 12, padding: 24, width: 420, border: "0.5px solid var(--color-border-secondary)", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" },
  modalTitle: { fontWeight: 700, color: P.nv, marginBottom: 4 },
  modalIntro: { color: "var(--color-text-secondary)", marginBottom: 16 },
  modalDetailPanel: { background: "#E6F4E8", borderRadius: 8, padding: "10px 14px", marginBottom: 16, border: "0.5px solid #BFE3C6" },
  modalDetailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  modalDetailLabel: { color: "var(--color-text-secondary)" },
  modalDetailVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },
  modalDetailValPlain: { fontWeight: 600, color: P.nv },
  modalEmailPanel: { background: "var(--color-background-secondary)", borderRadius: 7, padding: "9px 12px", marginBottom: 16, border: "0.5px solid var(--color-border-tertiary)" },
  modalEmailLabel: { color: "var(--color-text-secondary)", marginBottom: 3 },
  modalEmailVal: { fontWeight: 600, color: P.nv },
  modalNote: { color: "var(--color-text-secondary)", marginBottom: 16 },
  modalActions: { display: "flex", gap: 10 },
  modalSendBtn: { flex: 1, textTransform: "none", color: "#fff", backgroundColor: P.nv, fontWeight: 700, "&:hover": { backgroundColor: "#241C8A" } },
  modalCancelBtn: { textTransform: "none", color: "var(--color-text-secondary)", border: "0.5px solid var(--color-border-secondary)" },
});

export default function LeakageTab({ period }) {
  const cls = useStyles();
  const d = PD[period], lk = d.lk;
  const [drill, setDrill] = useState(null);

  const [ticketPlate, setTicketPlate] = useState(null);
  const [ticketSent, setTicketSent] = useState({});
  const DM_EMAIL = "dm@grandhyatt.com.au";

  const categories = [
    {id:"foc",   l:"Free of Charge",       cnt:lk.foc,                 rv:lk.fr, pct:59, c:P.nv},
    {id:"buzz",  l:"Buzz-outs",            cnt:lk.bo,                  rv:lk.br, pct:26, c:"#3127B8"},
    {id:"manual",l:"Front Desk Leakage",   cnt:Math.round(lk.foc*.14), rv:lk.mr, pct:13, c:"#3127B8"},
    {id:"over",  l:"Uncaptured Overstays", cnt:Math.round(lk.foc*.02), rv:lk.or, pct:2,  c:"#3EAC4D"},
  ];

  // Front desk leakage = DRIVO chargeback bookings that have no matching PMS charge
  // Source: DRIVO system chargeback count vs PMS parking charges count from night audit
  const DRIVO_CHARGEBACK_COUNT = 64;  // bookings DRIVO expected PMS to charge
  const PMS_PARKING_COUNT = 38;       // actual charges posted in PMS (from night audit)
  const fdMismatch = Math.max(0, DRIVO_CHARGEBACK_COUNT - PMS_PARKING_COUNT);
  const fdAvgCharge = 28;             // average parking charge ($)
  const fdLeakage = fdMismatch * fdAvgCharge;
  const contractorLeakage = Math.round(lk.tot * 0.14);

  const SANCTIONED = [
    {plate:"HTL 001",label:"GM Vehicle",   days:8, bays:1,hoursPerDay:7.2,estLost:0},
    {plate:"HTL 002",label:"Maintenance",  days:12,bays:1,hoursPerDay:6.1,estLost:0},
    {plate:"HTL 003",label:"F&B Supplier", days:9, bays:1,hoursPerDay:4.8,estLost:144},
    {plate:"EXC 441",label:"Corp Account", days:11,bays:1,hoursPerDay:9.1,estLost:218},
    {plate:"VIP 220",label:"VIP Guest",    days:7, bays:2,hoursPerDay:8.4,estLost:336},
    {plate:"CON 881",label:"Contractor",   days:10,bays:1,hoursPerDay:5.5,estLost:0},
  ];

  const REPEAT_BUZZOUTS = [
    {plate:"WAL 991",count:2,totalLost:72, lastDate:"10 Apr",lastStaff:"Elena R.",  avoidable:true},
    {plate:"UNK 881",count:1,totalLost:34, lastDate:"12 Apr",lastStaff:"David W.",  avoidable:true},
    {plate:"MAZ 901",count:1,totalLost:24, lastDate:"11 Apr",lastStaff:"James T.",  avoidable:false},
    {plate:"FOR 234",count:1,totalLost:18, lastDate:"12 Apr",lastStaff:"Marcus L.", avoidable:false},
    {plate:"BRT 555",count:1,totalLost:16, lastDate:"11 Apr",lastStaff:"Marcus L.", avoidable:true},
  ];

  const sanctionedEstLost = SANCTIONED.reduce((a,s)=>a+s.estLost,0);
  const sanctionedTotalHours = SANCTIONED.reduce((a,s)=>a+(s.days*s.hoursPerDay*s.bays),0);

  return (
    <Box className={cls.root}>
      <Box className={cls.kpiGrid}>
        <Kpi label="Total Leakage"        qualifiers={["Estimated"]} value={fmt(lk.tot)} delta={3.2} up={false} invertColor period={period} />
        <Kpi label="% of Revenue"         qualifiers={["Estimated"]} value={lk.pct+"%"} delta={1.1} up={false} invertColor period={period} />
        <Kpi label="FOC Guest Cards"      value={lk.foc.toString()}                    delta={8.4} up={false} invertColor period={period} />
        <Kpi label="Front Desk Leakage"   value={"$"+fdLeakage.toLocaleString()}       delta={5.2} up={false} invertColor period={period} />
        <Kpi label="Buzz-outs"            value={lk.bo.toString()}                     delta={4.5} up={false} invertColor period={period} />
        <Kpi label="Contractors"          value={"$"+contractorLeakage.toLocaleString()} delta={2.1} up={false} invertColor period={period} />
        <Kpi label="Uncaptured Overstays" value={"$"+lk.or.toLocaleString()}           delta={1.8} up={false} invertColor period={period} />
      </Box>

      <Box className={cls.mainGrid}>

        <Card title="Leakage Breakdown" subtitle={`Click any category to drill in -- ${period}`}>
          <Paper elevation={0} className={cls.toBeatPanel}>
            <Typography variant="caption" component="div" className={cls.toBeatTitle}>TO BEAT {COMPARE_LABEL[period]}</Typography>
            <Box className={cls.toBeatList}>
              {(()=>{
                const days={"1W":7,"4W":28,"MTD":12,"QTD":12}[period]||30;
                return [
                  {a:`Cut FOC guest cards to under ${(lk.foc*.75/days).toFixed(1)}/day (currently ${(lk.foc/days).toFixed(1)}/day)`,gain:Math.round(lk.fr*.25)},
                  {a:`Reduce buzz-outs to under ${(lk.bo*.6/days).toFixed(1)}/day (currently ${(lk.bo/days).toFixed(1)}/day)`,      gain:Math.round(lk.br*.4)},
                  {a:`Fix ${Math.round(lk.foc*.14)} front desk entry errors (${(lk.foc*.14/days).toFixed(1)}/day)`,                 gain:lk.mr},
                ];
              })().map((item,i)=>(
                <Box key={i} className={cls.toBeatRow}>
                  <Typography variant="body2" component="span" className={cls.toBeatText}>{"* "+item.a}</Typography>
                  <Typography variant="body2" component="span" className={cls.toBeatGain}>{"+"+fmt(item.gain)}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {categories.map((it,i)=>(
            <Box key={i} onClick={()=>setDrill(drill===it.id?null:it.id)} className={cls.catRow} style={{background:drill===it.id?"var(--color-background-secondary)":"transparent",border:drill===it.id?"0.5px solid var(--color-border-secondary)":"0.5px solid transparent"}}>
              <Box className={cls.catHead}>
                <Box className={cls.catLeft}>
                  <span className={cls.catBar} style={{background:it.c}} />
                  <Typography variant="body2" component="span" className={cls.catLabel}>{it.l}</Typography>
                  <Badge color={it.c}>{it.cnt}</Badge>
                </Box>
                <Box className={cls.catRight}>
                  <Typography variant="body2" component="span" className={cls.catVal}>{"$"+it.rv.toLocaleString()}</Typography>
                  <Typography variant="caption" component="span" className={cls.catPct}>{it.pct}%</Typography>
                  <Typography variant="body2" component="span" className={cls.catChevron}>{drill===it.id?"^":"v"}</Typography>
                </Box>
              </Box>
              <ProgBar pct={it.pct} color={it.c} />

              {drill==="foc"&&it.id==="foc"&&(
                <Box className={cls.drillWrap}>
                  <Typography variant="caption" component="div" className={cls.drillTitle}>FREE OF CHARGE RECORDS</Typography>
                  <Table className={`${cls.table} ${cls.tableMin520}`}>
                    <TableHead><TableRow>{["Ref","Staff","Guest","Room","Plate","Bay","Time","Duration","Lost","Reason"].map(h=><TableCell key={h} className={cls.headCell}><Typography variant="caption" component="span">{h}</Typography></TableCell>)}</TableRow></TableHead>
                    <TableBody>{FOC_DETAILS.map((r,j)=>(
                      <TableRow key={j} className={cls.bodyRow}>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.monoRef}>{r.id}</Typography></TableCell>
                        <TableCell className={cls.cell}><Box className={cls.staffCellInner}><Typography variant="caption" component="div" className={cls.staffAvatar}>{ini(r.staff)}</Typography><Typography variant="body2" component="span">{r.staff}</Typography></Box></TableCell>
                        <TableCell className={cls.cell}><Typography variant="body2" component="span">{r.guest}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.room}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.plateChip}>{r.plate}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.bay}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.time}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.duration}</Typography></TableCell>
                        <TableCell className={cls.lostCell}><Typography variant="body2" component="span">{"$"+r.lost}</Typography></TableCell>
                        <TableCell className={`${cls.reasonCell} ${cls.reasonCell140}`}><Typography variant="caption" component="span">{r.reason}</Typography></TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </Box>
              )}

              {drill==="buzz"&&it.id==="buzz"&&(
                <Box className={cls.drillWrap}>
                  <Typography variant="caption" component="div" className={cls.drillTitle}>BUZZ-OUT RECORDS</Typography>
                  <Table className={cls.table}>
                    <TableHead><TableRow>{["Ref","Staff","Guest","Plate","Vehicle","Bay","Overstay","Lost","Reason"].map(h=><TableCell key={h} className={cls.headCell}><Typography variant="caption" component="span">{h}</Typography></TableCell>)}</TableRow></TableHead>
                    <TableBody>{BUZZOUT_DETAILS.map((r,j)=>(
                      <TableRow key={j} className={cls.bodyRow}>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.monoRefIndigo}>{r.id}</Typography></TableCell>
                        <TableCell className={cls.cell}><Box className={cls.staffCellInner}><Typography variant="caption" component="div" className={cls.staffAvatar}>{ini(r.staff)}</Typography><Typography variant="body2" component="span">{r.staff}</Typography></Box></TableCell>
                        <TableCell className={cls.cell}><Typography variant="body2" component="span">{r.guest}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.plateChip}>{r.plate}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.vehicle}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.bay}</Typography></TableCell>
                        <TableCell className={cls.overstayVal}><Typography variant="body2" component="span">{r.overstay}</Typography></TableCell>
                        <TableCell className={cls.lostCell}><Typography variant="body2" component="span">{"$"+r.lost}</Typography></TableCell>
                        <TableCell className={`${cls.reasonCell} ${cls.reasonCell120}`}><Typography variant="caption" component="span">{r.reason}</Typography></TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </Box>
              )}

              {drill==="over"&&it.id==="over"&&(
                <Box className={cls.drillWrap}>
                  <Typography variant="caption" component="div" className={cls.drillTitle}>UNCAPTURED OVERSTAY RECORDS</Typography>
                  <Table className={cls.table}>
                    <TableHead><TableRow>{["Ref","Guest","Room","Plate","Booked Until","Actual Exit","Overstay","Lost","Captured"].map(h=><TableCell key={h} className={cls.headCell}><Typography variant="caption" component="span">{h}</Typography></TableCell>)}</TableRow></TableHead>
                    <TableBody>{OVERSTAY_DETAILS.map((r,j)=>(
                      <TableRow key={j} className={cls.bodyRow}>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.monoRefIndigo}>{r.id}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="body2" component="span">{r.guest}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.room}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" className={cls.plateChip}>{r.plate}</Typography></TableCell>
                        <TableCell className={cls.cellSecondary}><Typography variant="body2" component="span">{r.checkOut}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="body2" component="span" className={cls.actualText}>{r.actual}</Typography></TableCell>
                        <TableCell className={cls.overstayVal}><Typography variant="body2" component="span">{r.overstay}</Typography></TableCell>
                        <TableCell className={cls.lostCell}><Typography variant="body2" component="span">{"$"+r.lost}</Typography></TableCell>
                        <TableCell className={cls.cell}><Typography variant="caption" component="span" style={{fontWeight:600,color:r.captured?"#241C8A":P.nv}}>{r.captured?"Yes":"No"}</Typography></TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          ))}
        </Card>

        <Card title="Front Desk Leakage -- PMS Reconciliation" subtitle="DRIVO chargeback bookings vs PMS parking charges posted">
          <Paper elevation={0} className={cls.howPanel}>
            <Typography variant="caption" component="div" className={cls.howTitle}>HOW THIS IS CALCULATED</Typography>
            <Typography variant="body2" component="div" className={cls.howBody}>When front desk creates a DRIVO booking but forgets to post the charge in PMS, revenue is lost. DRIVO compares its chargeback count to the night audit PMS count to find the gap.</Typography>
          </Paper>
          <Box className={cls.fdGrid}>
            {[
              {l:"DRIVO Chargeback Bookings", v:DRIVO_CHARGEBACK_COUNT, sub:"Expected PMS charges",     color:P.nv},
              {l:"PMS Charges Posted",         v:PMS_PARKING_COUNT,      sub:"From night audit entry",   color:"#3127B8"},
              {l:"Unmatched (Leakage)",        v:fdMismatch,             sub:fdMismatch>0?"Missing PMS charges":"No gap detected", color:fdMismatch>0?P.nv:"#241C8A"},
            ].map(({l,v,sub,color})=>(
              <Paper key={l} elevation={0} className={cls.fdTile}>
                <Typography variant="caption" component="div" className={cls.fdTileLabel}>{l}</Typography>
                <Typography variant="h5" component="div" className={cls.fdTileVal} style={{color}}>{v}</Typography>
                <Typography variant="caption" component="div" className={cls.fdTileSub}>{sub}</Typography>
              </Paper>
            ))}
          </Box>
          <Box className={cls.impactSection}>
            <Box className={cls.impactRow}>
              <Typography variant="body2" component="span" className={cls.impactLabel}>ESTIMATED REVENUE IMPACT</Typography>
              <Typography variant="body2" component="span" className={cls.impactVal}>{"$"+fdLeakage.toLocaleString()}</Typography>
            </Box>
            <Box className={cls.impactGrid}>
              {[
                ["Avg Parking Charge","$"+fdAvgCharge+"/booking"],
                ["Unmatched Bookings",fdMismatch+" bookings"],
                ["Estimated Lost","$"+fdLeakage.toLocaleString()],
                ["Fix","Post missing PMS charges"],
              ].map(([l,v])=>(
                <Box key={l} className={cls.impactItem}>
                  <Typography variant="caption" component="span" className={cls.impactItemLabel}>{l}</Typography>
                  <Typography variant="caption" component="span" className={cls.impactItemVal}>{v}</Typography>
                </Box>
              ))}
            </Box>
            {fdMismatch>0&&<Typography variant="caption" component="div" className={cls.actionPanel}>Action: Cross-check DRIVO chargeback report against PMS parking journal for {fdMismatch} unmatched {fdMismatch===1?"booking":"bookings"} and post missing charges before close of business.</Typography>}
          </Box>
        </Card>

        <Card title="Buzz-out Repeat Offenders" subtitle={`Plates with recurring uncharged exits -- ${period}`}>
            <Table className={cls.table}>
              <TableHead>
                <TableRow className={cls.headRow}>
                  {["Plate","Incidents","Total Lost","Last Exit","Staff","Action"].map(h=>(
                    <TableCell key={h} className={cls.headCellSpaced}><Typography variant="caption" component="span">{h}</Typography></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {REPEAT_BUZZOUTS.map((r,i)=>(
                  <TableRow key={i} className={cls.bodyRow} style={{background:r.count>1?P.nv+"08":"transparent"}}>
                    <TableCell className={cls.bigCell}>
                      <Typography variant="body2" component="span" className={cls.plateBadge}>{r.plate}</Typography>
                      {r.count>1&&<Typography variant="caption" component="span" className={cls.countBadge}>{r.count+"x"}</Typography>}
                    </TableCell>
                    <TableCell className={cls.bigCellMono}><Typography variant="body2" component="span">{r.count}</Typography></TableCell>
                    <TableCell className={cls.bigCellMono}><Typography variant="body2" component="span">{"$"+r.totalLost}</Typography></TableCell>
                    <TableCell className={cls.bigCellSecondary}><Typography variant="body2" component="span">{r.lastDate}</Typography></TableCell>
                    <TableCell className={cls.bigCellSecondary}><Typography variant="body2" component="span">{r.lastStaff}</Typography></TableCell>
                    <TableCell className={cls.bigCell}>
                      {r.avoidable
                        ? ticketSent[r.plate]
                          ? <Typography variant="caption" component="span" className={cls.ticketSentText}>v Ticket sent</Typography>
                          : <Button size="small" variant="contained" disableElevation className={cls.flagBtn} onClick={()=>setTicketPlate(r)}>Flag for review</Button>
                        : <Typography variant="caption" component="span" className={cls.borderlineText}>Borderline</Typography>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

        <Card title="Leakage Trend" subtitle={`${period} vs prior period`}>
          <Box className={cls.trendChart}>
            <InsightsChart type="area" data={d.chart} xKey="m" height={180}
              series={[
                {key:"plk",name:"Prior period",color:"#3EAC4D",fill:"#E6F4E8",width:1.5,dash:5},
                {key:"lk",name:"Current period",color:P.nv,fill:"#BFE3C6",width:2.5},
              ]} />
          </Box>
        </Card>

        <Card title="Sanctioned Plate Usage" subtitle={`Authorised vehicles -- ${period}`}>
              <Box className={cls.sanctGrid}>
                {[["Sanctioned Plates",SANCTIONED.length],["Total Bay-Hours",Math.round(sanctionedTotalHours)+"h"],["Opp. Cost","$"+sanctionedEstLost]].map(([l,v])=>(
                  <Paper key={l} elevation={0} className={cls.sanctTile}>
                    <Typography variant="caption" component="div" className={cls.sanctTileLabel}>{l}</Typography>
                    <Typography variant="subtitle1" component="div" className={cls.sanctTileVal}>{v}</Typography>
                  </Paper>
                ))}
              </Box>
              <Table className={cls.table}>
                <TableHead>
                  <TableRow className={cls.headRow}>
                    {["Plate","Label","Days","Bays","Hrs/Day","Bay-Hrs","Opp. Cost"].map(h=>(
                      <TableCell key={h} className={cls.headCellSpaced}><Typography variant="caption" component="span">{h}</Typography></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...SANCTIONED].sort((a,b)=>b.estLost-a.estLost).map((s,i)=>(
                    <TableRow key={i} className={cls.bodyRow}>
                      <TableCell className={cls.sanctCell}><Typography variant="caption" component="span" className={cls.sanctPlate}>{s.plate}</Typography></TableCell>
                      <TableCell className={cls.sanctLabel}><Typography variant="caption" component="span">{s.label}</Typography></TableCell>
                      <TableCell className={cls.sanctMono}><Typography variant="body2" component="span">{s.days}</Typography></TableCell>
                      <TableCell className={cls.sanctMono}><Typography variant="body2" component="span">{s.bays}</Typography></TableCell>
                      <TableCell className={cls.sanctMonoSecondary}><Typography variant="body2" component="span">{s.hoursPerDay+"h"}</Typography></TableCell>
                      <TableCell className={cls.sanctMonoBold}><Typography variant="body2" component="span">{Math.round(s.days*s.hoursPerDay*s.bays)+"h"}</Typography></TableCell>
                      <TableCell className={cls.sanctCell} style={{fontFamily:"var(--font-mono)"}}><Typography variant="body2" component="span" style={{fontWeight:s.estLost>0?700:400,color:s.estLost>0?P.nv:"var(--color-text-tertiary)",fontFamily:"var(--font-mono)"}}>{s.estLost>0?"$"+s.estLost:"--"}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

      </Box>

      {ticketPlate && (
        <Box className={cls.overlay} onClick={()=>setTicketPlate(null)}>
          <Paper elevation={0} onClick={e=>e.stopPropagation()} className={cls.modal}>
            <Typography variant="subtitle1" component="div" className={cls.modalTitle}>Flag Plate for DRIVO Review</Typography>
            <Typography variant="body2" component="div" className={cls.modalIntro}>A support ticket will be created and DRIVO will email plate imagery and incident details to the DM on file.</Typography>
            <Paper elevation={0} className={cls.modalDetailPanel}>
              <Box className={cls.modalDetailGrid}>
                <div>
                  <Typography variant="caption" component="div" className={cls.modalDetailLabel}>Plate</Typography>
                  <Typography variant="subtitle1" component="div" className={cls.modalDetailVal}>{ticketPlate.plate}</Typography>
                </div>
                <div>
                  <Typography variant="caption" component="div" className={cls.modalDetailLabel}>Incidents</Typography>
                  <Typography variant="subtitle1" component="div" className={cls.modalDetailVal}>{ticketPlate.count}x</Typography>
                </div>
                <div>
                  <Typography variant="caption" component="div" className={cls.modalDetailLabel}>Total Revenue Lost</Typography>
                  <Typography variant="subtitle1" component="div" className={cls.modalDetailVal}>{"$"+ticketPlate.totalLost}</Typography>
                </div>
                <div>
                  <Typography variant="caption" component="div" className={cls.modalDetailLabel}>Last Incident</Typography>
                  <Typography variant="body2" component="div" className={cls.modalDetailValPlain}>{ticketPlate.lastDate} . {ticketPlate.lastStaff}</Typography>
                </div>
              </Box>
            </Paper>
            <Paper elevation={0} className={cls.modalEmailPanel}>
              <Typography variant="caption" component="div" className={cls.modalEmailLabel}>Ticket will be sent to DM email on file</Typography>
              <Typography variant="body2" component="div" className={cls.modalEmailVal}>{DM_EMAIL}</Typography>
            </Paper>
            <Typography variant="body2" component="div" className={cls.modalNote}>DRIVO support will retrieve CCTV imagery for the flagged plate and include incident timestamps, bay location, and staff member in the report.</Typography>
            <Box className={cls.modalActions}>
              <Button variant="contained" disableElevation className={cls.modalSendBtn} onClick={()=>{setTicketSent({...ticketSent,[ticketPlate.plate]:true});setTicketPlate(null);}}>Send to DRIVO Support</Button>
              <Button variant="outlined" disableElevation className={cls.modalCancelBtn} onClick={()=>setTicketPlate(null)}>Cancel</Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
